import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { PendingUsersBanner } from '../../components/PendingUsersBanner';
import { Document, Page, pdfjs } from 'react-pdf';
import {
  API_BASE,
  Room,
  Story,
  getProjects,
  Project,
  getRooms,
  getStories,
  createRoom,
  createPageMapping,
  createStoryPageMapping,
  BoundingBox,
  detectOCRRegion,
  OCRRegion,
  saveOCRRegions,
} from '../../api/client';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

interface RoomEntry {
  id: string;
  number: string;
  name: string;
}

export function RoomMappingWorkflow() {
  const { projectId, documentId } = useParams<{ projectId: string; documentId: string }>();
  const { token, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [project, setProject] = useState<Project | null>(null);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [stories, setStories] = useState<Story[]>([]);
  const [loading, setLoading] = useState(true);
  const [mappingMode, setMappingMode] = useState<'rooms' | 'floor'>('rooms');
  const [selectedStory, setSelectedStory] = useState<Story | null>(null);
  const [storySearch, setStorySearch] = useState('');
  const [numPages, setNumPages] = useState<number>(1);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [jumpToPage, setJumpToPage] = useState<string>('1');
  const [saving, setSaving] = useState(false);
  const [label, setLabel] = useState<string>('Floor Plans');

  // Room entries for current page
  const [roomEntries, setRoomEntries] = useState<RoomEntry[]>([]);

  // Saved mappings per page
  const [savedMappings, setSavedMappings] = useState<Map<number, RoomEntry[]>>(new Map());

  // Drawing state
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState<{ x: number; y: number } | null>(null);
  const [currentBox, setCurrentBox] = useState<BoundingBox | null>(null);
  const [drawnBoxes, setDrawnBoxes] = useState<BoundingBox[]>([]);

  // OCR state
  const [storedRegions, setStoredRegions] = useState<OCRRegion[]>([]);
  const [ocrLoading, setOcrLoading] = useState(false);

  // Zoom and pan state
  const [zoomLevel, setZoomLevel] = useState<number>(1);
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState<{ x: number; y: number } | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Bulk mapping state
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkStartPage, setBulkStartPage] = useState(1);
  const [bulkEndPage, setBulkEndPage] = useState(1);
  const [bulkResults, setBulkResults] = useState<Map<number, RoomEntry[]>>(new Map());
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0 });

  // PDF dimensions
  const [pdfDimensions, setPdfDimensions] = useState<{ width: number; height: number } | null>(null);
  const [originalPdfDimensions, setOriginalPdfDimensions] = useState<{ width: number; height: number } | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const lastInputRef = useRef<HTMLInputElement>(null);
  const [lastEntryCount, setLastEntryCount] = useState(0);

  // Dynamic PDF width based on browser width (minus side panel + margins + padding)
  const [pdfMaxWidth, setPdfMaxWidth] = useState(window.innerWidth - 520);

  useEffect(() => {
    const handleResize = () => {
      setPdfMaxWidth(window.innerWidth - 520);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Space key for pan mode
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space' && !e.repeat) {
        e.preventDefault();
        setIsPanning(true);
      }
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        setIsPanning(false);
        setPanStart(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // Recalculate display dimensions when window resizes
  useEffect(() => {
    if (originalPdfDimensions) {
      const displayWidth = pdfMaxWidth;
      const scaleFactor = displayWidth / originalPdfDimensions.width;
      const displayHeight = originalPdfDimensions.height * scaleFactor;

      setPdfDimensions({ width: displayWidth, height: displayHeight });
    }
  }, [pdfMaxWidth, originalPdfDimensions]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin/login');
      return;
    }
    loadData();
  }, [projectId, token, isAuthenticated, navigate]);

  const loadData = async () => {
    if (!token || !projectId) return;
    try {
      const [projects, roomsData, storiesData] = await Promise.all([
        getProjects(token),
        getRooms(token, parseInt(projectId)),
        getStories(token, parseInt(projectId))
      ]);
      const foundProject = projects.find(p => p.id === parseInt(projectId));
      if (foundProject) {
        setProject(foundProject);
        setRooms(roomsData);
        setStories(storiesData);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Auto-OCR when navigating to a new page (if regions exist)
  useEffect(() => {
    if (storedRegions.length > 0 && currentPage > 1 && documentId) {
      runAutoOCR();
    }
  }, [currentPage]);

  const runAutoOCR = async () => {
    if (!documentId || storedRegions.length === 0) return;

    setOcrLoading(true);
    setDrawnBoxes([...storedRegions]);

    try {
      const allEntries: RoomEntry[] = [];

      for (const region of storedRegions) {
        // Regions already stored in original coords
        const result = await detectOCRRegion(parseInt(documentId), currentPage, region);
        const entries = result.rooms.map(room => ({
          id: `entry-${Date.now()}-${Math.random()}`,
          number: room.number,
          name: room.name || ''
        }));
        allEntries.push(...entries);
      }

      if (allEntries.length > 0) {
        setRoomEntries(allEntries);
      }
    } catch (error) {
      console.error('Auto-OCR failed:', error);
    } finally {
      setOcrLoading(false);
    }
  };

  // Focus last input only when a new entry is added
  useEffect(() => {
    if (roomEntries.length > lastEntryCount && lastInputRef.current) {
      lastInputRef.current.focus();
    }
    setLastEntryCount(roomEntries.length);
  }, [roomEntries.length]);

  // Redraw canvas when boxes change
  useEffect(() => {
    if (pdfDimensions && canvasRef.current) {
      redrawCanvas();
    }
  }, [drawnBoxes, currentBox, pdfDimensions, zoomLevel]);

  const redrawCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas || !pdfDimensions) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.lineWidth = 2;
    ctx.strokeStyle = '#28a745';
    ctx.fillStyle = 'rgba(40, 167, 69, 0.2)';

    // Convert stored original coords to display coords for rendering
    drawnBoxes.forEach(box => {
      const displayBox = toDisplayCoords(box);
      ctx.fillRect(displayBox.x, displayBox.y, displayBox.width, displayBox.height);
      ctx.strokeRect(displayBox.x, displayBox.y, displayBox.width, displayBox.height);
    });

    if (currentBox) {
      ctx.setLineDash([5, 5]);
      ctx.fillRect(currentBox.x, currentBox.y, currentBox.width, currentBox.height);
      ctx.strokeRect(currentBox.x, currentBox.y, currentBox.width, currentBox.height);
      ctx.setLineDash([]);
    }
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (isPanning) {
      setPanStart({ x: e.clientX, y: e.clientY });
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    // Divide by zoomLevel to convert from scaled space to canvas native space
    const x = (e.clientX - rect.left) / zoomLevel;
    const y = (e.clientY - rect.top) / zoomLevel;
    setIsDrawing(true);
    setStartPoint({ x, y });
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (isPanning && panStart && scrollContainerRef.current) {
      const dx = panStart.x - e.clientX;
      const dy = panStart.y - e.clientY;
      scrollContainerRef.current.scrollLeft += dx;
      scrollContainerRef.current.scrollTop += dy;
      setPanStart({ x: e.clientX, y: e.clientY });
      return;
    }

    if (!isDrawing || !startPoint) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    // Divide by zoomLevel to convert from scaled space to canvas native space
    const currentX = (e.clientX - rect.left) / zoomLevel;
    const currentY = (e.clientY - rect.top) / zoomLevel;
    const x = Math.min(startPoint.x, currentX);
    const y = Math.min(startPoint.y, currentY);
    const width = Math.abs(currentX - startPoint.x);
    const height = Math.abs(currentY - startPoint.y);
    setCurrentBox({ x, y, width, height });
  };

  const handleMouseUp = async () => {
    if (isPanning) {
      setPanStart(null);
      return;
    }

    if (!isDrawing || !currentBox) {
      setIsDrawing(false);
      setStartPoint(null);
      return;
    }

    if (currentBox.width > 10 && currentBox.height > 10 && documentId) {
      // Immediately mark as not drawing to prevent duplicate calls from onMouseUp + onMouseLeave
      setIsDrawing(false);
      setStartPoint(null);
      const boxToProcess = currentBox;
      setCurrentBox(null);

      // Convert display coordinates to original PDF coordinates for storage
      const originalBox = toOriginalCoords(boxToProcess);
      setDrawnBoxes([...drawnBoxes, originalBox]);

      // Call OCR on the drawn region (originalBox already in original coords)
      setOcrLoading(true);
      try {
        const result = await detectOCRRegion(parseInt(documentId), currentPage, originalBox);

        // Add room entries from OCR result
        const newEntries = result.rooms.map(room => ({
          id: `entry-${Date.now()}-${Math.random()}`,
          number: room.number,
          name: room.name || ''
        }));

        if (newEntries.length > 0) {
          setRoomEntries([...roomEntries, ...newEntries]);
        } else {
          // No rooms detected, add empty entry
          setRoomEntries([...roomEntries, { id: `entry-${Date.now()}`, number: '', name: '' }]);
        }

        // Store the region for auto-detection on later pages (in original coords)
        setStoredRegions([...storedRegions, originalBox]);
      } catch (error) {
        console.error('OCR failed:', error);
        setRoomEntries([...roomEntries, { id: `entry-${Date.now()}`, number: '', name: '' }]);
      } finally {
        setOcrLoading(false);
      }
    }

    setIsDrawing(false);
    setStartPoint(null);
    setCurrentBox(null);
  };

  const handlePdfLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };

  const handlePageLoadSuccess = (page: any) => {
    const viewport = page.getViewport({ scale: 1.0 });
    const originalWidth = viewport.width;
    const originalHeight = viewport.height;

    // Store original dimensions for coordinate conversion
    setOriginalPdfDimensions({ width: originalWidth, height: originalHeight });

    // Page component always renders at pdfMaxWidth (react-pdf scales to fit)
    const displayWidth = pdfMaxWidth;
    const scaleFactor = displayWidth / originalWidth;
    const displayHeight = originalHeight * scaleFactor;

    setPdfDimensions({ width: displayWidth, height: displayHeight });
  };

  // Convert canvas coordinates to original PDF coordinates for storage/OCR
  const toOriginalCoords = (box: BoundingBox): BoundingBox => {
    if (!pdfDimensions || !originalPdfDimensions) return box;
    const scale = originalPdfDimensions.width / pdfDimensions.width;
    return {
      x: box.x * scale,
      y: box.y * scale,
      width: box.width * scale,
      height: box.height * scale
    };
  };

  // Convert original PDF coordinates to canvas coordinates for rendering
  const toDisplayCoords = (box: BoundingBox): BoundingBox => {
    if (!pdfDimensions || !originalPdfDimensions) return box;
    const scale = pdfDimensions.width / originalPdfDimensions.width;
    return {
      x: box.x * scale,
      y: box.y * scale,
      width: box.width * scale,
      height: box.height * scale
    };
  };

  const updateRoomEntry = (id: string, field: 'number' | 'name', value: string) => {
    setRoomEntries(entries =>
      entries.map(e => {
        if (e.id !== id) return e;

        const updated = { ...e, [field]: value };

        // Auto-populate name when room number is typed and matches an existing room
        if (field === 'number' && value.trim()) {
          const existingRoom = rooms.find(r => r.number === value.trim());
          if (existingRoom && existingRoom.name) {
            updated.name = existingRoom.name;
          }
        }

        return updated;
      })
    );
  };

  const removeRoomEntry = (id: string) => {
    const index = roomEntries.findIndex(e => e.id === id);
    setRoomEntries(entries => entries.filter(e => e.id !== id));
    if (index !== -1) {
      setDrawnBoxes(boxes => boxes.filter((_, i) => i !== index));
    }
  };

  const addEmptyEntry = () => {
    setRoomEntries([...roomEntries, { id: `entry-${Date.now()}`, number: '', name: '' }]);
  };

  const saveAndAdvance = async (advance: boolean) => {
    if (!token || !documentId || !projectId) return;

    // Floor mode: save story-level mapping
    if (mappingMode === 'floor' && selectedStory) {
      setSaving(true);
      try {
        await createStoryPageMapping(token, selectedStory.id, parseInt(documentId), currentPage, label || undefined);

        setSavedMappings(prev => {
          const newMap = new Map(prev);
          const storyEntry: RoomEntry = { id: `story-${selectedStory.id}`, number: `Floor: ${selectedStory.name}`, name: '' };
          newMap.set(currentPage, [...(newMap.get(currentPage) || []), storyEntry]);
          return newMap;
        });

        if (advance && currentPage < numPages) {
          goToPage(currentPage + 1);
        }
      } catch (error) {
        console.error('Failed to save story mapping:', error);
        alert('Failed to save floor mapping');
      } finally {
        setSaving(false);
      }
      return;
    }

    const validEntries = roomEntries.filter(e => e.number.trim());
    if (validEntries.length === 0) {
      if (advance && currentPage < numPages) {
        goToPage(currentPage + 1);
      }
      return;
    }

    setSaving(true);
    try {
      // Save page mappings
      for (const entry of validEntries) {
        let room = rooms.find(r => r.number === entry.number.trim());
        if (!room) {
          room = await createRoom(token, parseInt(projectId), entry.number.trim(), entry.name.trim() || undefined);
          setRooms(prev => [...prev, room!]);
        }
        await createPageMapping(token, room.id, parseInt(documentId), currentPage, label || undefined);
      }

      // Save drawn boxes as OCR regions for auto-detection on future uploads
      if (drawnBoxes.length > 0) {
        try {
          // Convert drawn boxes to original PDF coordinates (normalized 0-1)
          const ocrRegions = drawnBoxes.map(box => toOriginalCoords(box));
          await saveOCRRegions(token, parseInt(documentId), ocrRegions);
          console.log('Saved', ocrRegions.length, 'OCR regions for auto-detection');
        } catch (ocrError) {
          console.error('Failed to save OCR regions:', ocrError);
          // Don't fail the whole operation if OCR save fails
        }
      }

      setSavedMappings(prev => {
        const newMap = new Map(prev);
        newMap.set(currentPage, validEntries);
        return newMap;
      });

      setRoomEntries([]);
      setDrawnBoxes([]);

      if (advance && currentPage < numPages) {
        goToPage(currentPage + 1);
      }
    } catch (error) {
      console.error('Failed to save:', error);
      alert('Failed to save room mappings');
    } finally {
      setSaving(false);
    }
  };

  const goToPage = (page: number) => {
    const p = Math.max(1, Math.min(numPages, page));
    setCurrentPage(p);
    setJumpToPage(p.toString());
    setRoomEntries([]);
    setDrawnBoxes([]);
  };

  const handleJumpToPage = () => {
    const page = parseInt(jumpToPage);
    if (!isNaN(page)) {
      goToPage(page);
    }
  };

  const handleZoomIn = () => {
    setZoomLevel(z => Math.min(2.5, z + 0.25));
  };

  const handleZoomOut = () => {
    setZoomLevel(z => Math.max(0.5, z - 0.25));
  };

  const openBulkModal = () => {
    setBulkStartPage(1);
    setBulkEndPage(numPages);
    setBulkResults(new Map());
    setBulkProgress({ current: 0, total: 0 });
    setShowBulkModal(true);
  };

  const runBulkOCR = async () => {
    if (!documentId || storedRegions.length === 0) return;

    setBulkProcessing(true);
    const total = bulkEndPage - bulkStartPage + 1;
    setBulkProgress({ current: 0, total });
    const results = new Map<number, RoomEntry[]>();

    for (let page = bulkStartPage; page <= bulkEndPage; page++) {
      setBulkProgress({ current: page - bulkStartPage + 1, total });

      try {
        const pageEntries: RoomEntry[] = [];
        for (const region of storedRegions) {
          const result = await detectOCRRegion(parseInt(documentId), page, region);
          const entries = result.rooms.map(room => ({
            id: `entry-${Date.now()}-${Math.random()}`,
            number: room.number,
            name: room.name || ''
          }));
          pageEntries.push(...entries);
        }
        if (pageEntries.length > 0) {
          results.set(page, pageEntries);
        }
      } catch (error) {
        console.error(`OCR failed for page ${page}:`, error);
      }
    }

    setBulkResults(results);
    setBulkProcessing(false);
  };

  const saveBulkResults = async () => {
    if (!token || !documentId || !projectId) return;

    setBulkProcessing(true);
    let lastPage = bulkStartPage;

    try {
      for (const [page, entries] of Array.from(bulkResults.entries())) {
        const validEntries = entries.filter((e: RoomEntry) => e.number.trim());
        for (const entry of validEntries) {
          let room = rooms.find(r => r.number === entry.number.trim());
          if (!room) {
            room = await createRoom(token, parseInt(projectId), entry.number.trim(), entry.name.trim() || undefined);
            setRooms(prev => [...prev, room!]);
          }
          await createPageMapping(token, room.id, parseInt(documentId), page, label || undefined);
        }

        setSavedMappings(prev => {
          const newMap = new Map(prev);
          newMap.set(page, validEntries);
          return newMap;
        });

        lastPage = page;
      }

      setShowBulkModal(false);
      goToPage(lastPage);
    } catch (error) {
      console.error('Failed to save bulk results:', error);
      alert('Failed to save some room mappings');
    } finally {
      setBulkProcessing(false);
    }
  };

  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    // Only zoom if Shift is held, otherwise allow native scroll/pan
    if (!e.shiftKey) return;
    if (!scrollContainerRef.current || !pdfDimensions) return;

    e.preventDefault();

    const container = scrollContainerRef.current;
    const rect = container.getBoundingClientRect();

    // Mouse position relative to container
    const mouseX = e.clientX - rect.left + container.scrollLeft;
    const mouseY = e.clientY - rect.top + container.scrollTop;

    // Calculate new zoom level (0.05 for smooth scrolling)
    const delta = e.deltaY > 0 ? -0.05 : 0.05;
    const newZoom = Math.max(0.5, Math.min(2.5, zoomLevel + delta));

    if (newZoom === zoomLevel) return;

    // Calculate scroll position to keep mouse point stationary
    const scale = newZoom / zoomLevel;
    const newScrollLeft = mouseX * scale - (e.clientX - rect.left);
    const newScrollTop = mouseY * scale - (e.clientY - rect.top);

    setZoomLevel(newZoom);

    // Apply scroll after state update
    requestAnimationFrame(() => {
      container.scrollLeft = newScrollLeft;
      container.scrollTop = newScrollTop;
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !saving) {
      e.preventDefault();
      if (e.shiftKey) {
        saveAndAdvance(false);
      } else {
        saveAndAdvance(true);
      }
    } else if (e.key === 'Escape') {
      setDrawnBoxes([]);
      setRoomEntries([]);
    }
  };

  if (!isAuthenticated || loading) {
    return <div style={{ fontFamily: 'system-ui', padding: '24px' }}>Loading...</div>;
  }

  const pageMappings = savedMappings.get(currentPage) || [];
  const totalMapped = Array.from(savedMappings.values()).reduce((sum, arr) => sum + arr.length, 0);

  return (
    <div style={{ fontFamily: 'system-ui' }} onKeyDown={handleKeyDown} tabIndex={0}>
      <header style={{
        padding: '16px 24px',
        background: '#1a1a2e',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        gap: '16px'
      }}>
        <button
          onClick={() => navigate(`/admin/projects/${projectId}`)}
          style={{
            background: 'transparent',
            border: '1px solid #fff',
            color: '#fff',
            padding: '8px 16px',
            cursor: 'pointer',
            borderRadius: '4px'
          }}
        >
          Back to Project
        </button>
        <div>
          <h1 style={{ margin: 0, fontSize: '20px' }}>Map Rooms</h1>
          <p style={{ margin: '4px 0 0 0', fontSize: '14px', opacity: 0.8 }}>
            {project?.name} - {totalMapped} rooms mapped
          </p>
        </div>
      </header>

      <PendingUsersBanner />

      <main style={{ padding: '24px' }}>
        <div style={{ display: 'flex', gap: '24px' }}>
          {/* PDF Panel */}
          <div style={{ flex: 1, background: '#f5f5f5', borderRadius: '8px', padding: '16px' }}>
            {/* Navigation */}
            <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage <= 1}
                style={{
                  background: currentPage <= 1 ? '#ccc' : '#007bff',
                  color: '#fff',
                  border: 'none',
                  padding: '8px 16px',
                  cursor: currentPage <= 1 ? 'not-allowed' : 'pointer',
                  borderRadius: '4px'
                }}
              >
                Prev
              </button>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>Page</span>
                <input
                  type="text"
                  value={jumpToPage}
                  onChange={(e) => setJumpToPage(e.target.value)}
                  onBlur={handleJumpToPage}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); handleJumpToPage(); } }}
                  style={{ width: '50px', padding: '4px 8px', textAlign: 'center' }}
                />
                <span>of {numPages}</span>
              </div>

              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage >= numPages}
                style={{
                  background: currentPage >= numPages ? '#ccc' : '#007bff',
                  color: '#fff',
                  border: 'none',
                  padding: '8px 16px',
                  cursor: currentPage >= numPages ? 'not-allowed' : 'pointer',
                  borderRadius: '4px'
                }}
              >
                Next
              </button>

              <button
                onClick={() => { setRoomEntries([]); setDrawnBoxes([]); goToPage(currentPage + 1); }}
                disabled={currentPage >= numPages}
                style={{
                  background: '#6c757d',
                  color: '#fff',
                  border: 'none',
                  padding: '8px 16px',
                  cursor: currentPage >= numPages ? 'not-allowed' : 'pointer',
                  borderRadius: '4px'
                }}
              >
                Skip
              </button>

              <div style={{ borderLeft: '1px solid #ccc', height: '24px', margin: '0 8px' }} />

              <button
                onClick={handleZoomOut}
                disabled={zoomLevel <= 0.5}
                style={{
                  background: zoomLevel <= 0.5 ? '#ccc' : '#6c757d',
                  color: '#fff',
                  border: 'none',
                  padding: '8px 12px',
                  cursor: zoomLevel <= 0.5 ? 'not-allowed' : 'pointer',
                  borderRadius: '4px',
                  fontWeight: 'bold'
                }}
              >
                -
              </button>
              <span style={{ minWidth: '50px', textAlign: 'center' }}>{Math.round(zoomLevel * 100)}%</span>
              <button
                onClick={handleZoomIn}
                disabled={zoomLevel >= 2.5}
                style={{
                  background: zoomLevel >= 2.5 ? '#ccc' : '#6c757d',
                  color: '#fff',
                  border: 'none',
                  padding: '8px 12px',
                  cursor: zoomLevel >= 2.5 ? 'not-allowed' : 'pointer',
                  borderRadius: '4px',
                  fontWeight: 'bold'
                }}
              >
                +
              </button>

              <div style={{ borderLeft: '1px solid #ccc', height: '24px', margin: '0 8px' }} />

              <button
                onClick={openBulkModal}
                disabled={storedRegions.length === 0}
                style={{
                  background: storedRegions.length === 0 ? '#ccc' : '#28a745',
                  color: '#fff',
                  border: 'none',
                  padding: '8px 16px',
                  cursor: storedRegions.length === 0 ? 'not-allowed' : 'pointer',
                  borderRadius: '4px'
                }}
                title={storedRegions.length === 0 ? 'Draw a box first to define the OCR region' : 'Map all pages using the drawn box(es)'}
              >
                Map All Pages
              </button>
            </div>

            {/* PDF with canvas overlay - scrollable viewport */}
            <div
              ref={scrollContainerRef}
              onWheel={handleWheel}
              style={{
                position: 'relative',
                overflow: 'auto',
                background: '#fff',
                border: '2px solid #ddd',
                borderRadius: '4px',
                maxWidth: pdfMaxWidth + 20,
                maxHeight: 'calc(100vh - 250px)',
                cursor: isPanning ? 'grabbing' : 'crosshair'
              }}
            >
              <div style={{
                width: pdfDimensions ? pdfDimensions.width * zoomLevel : pdfMaxWidth,
                height: pdfDimensions ? pdfDimensions.height * zoomLevel : 'auto',
                position: 'relative'
              }}>
                <div style={{
                  transform: `scale(${zoomLevel})`,
                  transformOrigin: 'top left',
                  position: 'relative'
                }}>
                  <Document
                    file={`${API_BASE}/api/pdf/document/${documentId}`}
                    onLoadSuccess={handlePdfLoadSuccess}
                    loading={<div style={{ padding: '40px', color: '#666' }}>Loading PDF...</div>}
                  >
                    <Page
                      pageNumber={currentPage}
                      onLoadSuccess={handlePageLoadSuccess}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                      width={pdfMaxWidth}
                    />
                  </Document>
                  {pdfDimensions && (
                    <canvas
                      ref={canvasRef}
                      width={pdfDimensions.width}
                      height={pdfDimensions.height}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: pdfDimensions.width,
                        height: pdfDimensions.height,
                        cursor: isPanning ? 'grabbing' : 'crosshair'
                      }}
                      onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                  />
                )}
                </div>
              </div>
            </div>

            <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px' }}>
              <p style={{ fontSize: '14px', color: '#666', margin: 0 }}>
                Draw boxes around room labels &bull; Space+drag to pan &bull; Shift+scroll to zoom
              </p>
              {drawnBoxes.length > 0 && (
                <button
                  onClick={() => { setDrawnBoxes([]); setRoomEntries([]); }}
                  style={{
                    background: '#dc3545',
                    color: '#fff',
                    border: 'none',
                    padding: '6px 12px',
                    cursor: 'pointer',
                    borderRadius: '4px',
                    fontSize: '13px'
                  }}
                >
                  Clear Boxes
                </button>
              )}
            </div>
          </div>

          {/* Room Entry Panel */}
          <div style={{ width: '350px', flexShrink: 0, background: '#fff', borderRadius: '8px', padding: '24px', border: '1px solid #ddd' }}>
            <h3 style={{ margin: '0 0 16px 0' }}>Page {currentPage} Rooms</h3>

            {ocrLoading && (
              <div style={{ background: '#fff3cd', padding: '12px', borderRadius: '4px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid #856404',
                  borderTopColor: 'transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                <span style={{ color: '#856404' }}>Running OCR...</span>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              </div>
            )}

            {pageMappings.length > 0 && (
              <div style={{ background: '#d4edda', padding: '12px', borderRadius: '4px', marginBottom: '16px' }}>
                <strong style={{ color: '#155724' }}>Already mapped:</strong>
                {pageMappings.map((m, i) => (
                  <div key={i} style={{
                    color: m.id.startsWith('story-') ? '#fff' : '#155724',
                    background: m.id.startsWith('story-') ? '#007bff' : 'transparent',
                    padding: m.id.startsWith('story-') ? '4px 10px' : '0',
                    borderRadius: m.id.startsWith('story-') ? '4px' : '0',
                    marginTop: '4px',
                    display: 'inline-block'
                  }}>
                    {m.number}{m.name && ` - ${m.name}`}
                  </div>
                ))}
              </div>
            )}

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>Label</label>
              <input
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Floor Plans, Electrical, etc."
                style={{ width: '100%', padding: '8px', boxSizing: 'border-box' }}
              />
            </div>

            {/* Mapping mode toggle */}
            <div style={{ display: 'flex', gap: '0', marginBottom: '12px' }}>
              <button
                onClick={() => setMappingMode('rooms')}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #007bff',
                  borderRadius: '4px 0 0 4px',
                  backgroundColor: mappingMode === 'rooms' ? '#007bff' : '#fff',
                  color: mappingMode === 'rooms' ? '#fff' : '#007bff',
                  cursor: 'pointer',
                  fontFamily: 'system-ui'
                }}
              >
                Map to Rooms
              </button>
              <button
                onClick={() => setMappingMode('floor')}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #007bff',
                  borderLeft: 'none',
                  borderRadius: '0 4px 4px 0',
                  backgroundColor: mappingMode === 'floor' ? '#007bff' : '#fff',
                  color: mappingMode === 'floor' ? '#fff' : '#007bff',
                  cursor: 'pointer',
                  fontFamily: 'system-ui'
                }}
              >
                Map to Floor
              </button>
            </div>

            {mappingMode === 'floor' && (
              <div style={{ marginBottom: '16px' }}>
                <input
                  type="text"
                  placeholder="Search floors..."
                  value={storySearch}
                  onChange={(e) => setStorySearch(e.target.value)}
                  style={{ width: '100%', padding: '8px', marginBottom: '8px', border: '1px solid #ddd', borderRadius: '4px', fontFamily: 'system-ui', boxSizing: 'border-box' }}
                />
                {stories
                  .filter(s => s.name.toLowerCase().includes(storySearch.toLowerCase()))
                  .map(story => (
                    <div
                      key={story.id}
                      onClick={() => setSelectedStory(story)}
                      style={{
                        padding: '8px 12px',
                        cursor: 'pointer',
                        backgroundColor: selectedStory?.id === story.id ? '#e3f2fd' : '#fff',
                        border: selectedStory?.id === story.id ? '2px solid #007bff' : '1px solid #eee',
                        borderRadius: '4px',
                        marginBottom: '4px',
                        fontFamily: 'system-ui'
                      }}
                    >
                      {story.name}
                    </div>
                  ))
                }
                {selectedStory && (
                  <div style={{
                    marginTop: '8px',
                    padding: '8px',
                    backgroundColor: '#e8f5e9',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}>
                    Selected: <strong>{selectedStory.name}</strong> (all rooms on this floor)
                  </div>
                )}
              </div>
            )}

            {mappingMode === 'rooms' && (
            <div style={{ marginBottom: '16px' }}>
              <strong>Rooms:</strong>
              {roomEntries.length === 0 && (
                <p style={{ color: '#666', fontSize: '14px' }}>Draw a box on the PDF or click "Add Room" below</p>
              )}
              {roomEntries.map((entry, index) => (
                <div key={entry.id} style={{ display: 'flex', gap: '8px', marginTop: '8px', alignItems: 'center' }}>
                  <input
                    type="text"
                    value={entry.number}
                    onChange={(e) => updateRoomEntry(entry.id, 'number', e.target.value)}
                    onKeyDown={(e) => { if (e.key !== 'Enter' && e.key !== 'Escape') e.stopPropagation(); }}
                    placeholder="Number"
                    style={{ width: '80px', padding: '8px' }}
                    ref={index === roomEntries.length - 1 ? lastInputRef : undefined}
                  />
                  <input
                    type="text"
                    value={entry.name}
                    onChange={(e) => updateRoomEntry(entry.id, 'name', e.target.value)}
                    onKeyDown={(e) => { if (e.key !== 'Enter' && e.key !== 'Escape') e.stopPropagation(); }}
                    placeholder="Name (optional)"
                    style={{ flex: 1, padding: '8px' }}
                  />
                  <button
                    onClick={() => removeRoomEntry(entry.id)}
                    style={{ background: '#dc3545', color: '#fff', border: 'none', padding: '8px 12px', cursor: 'pointer', borderRadius: '4px' }}
                  >
                    x
                  </button>
                </div>
              ))}
              <button
                onClick={addEmptyEntry}
                style={{ marginTop: '8px', background: 'transparent', border: '1px dashed #ccc', padding: '8px', width: '100%', cursor: 'pointer' }}
              >
                + Add Room
              </button>
            </div>
            )}

            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button
                onClick={() => saveAndAdvance(false)}
                disabled={saving || (mappingMode === 'rooms' ? roomEntries.filter(e => e.number.trim()).length === 0 : !selectedStory)}
                style={{
                  flex: 1,
                  background: '#17a2b8',
                  color: '#fff',
                  border: 'none',
                  padding: '12px',
                  cursor: 'pointer',
                  borderRadius: '4px',
                  opacity: saving ? 0.7 : 1
                }}
              >
                Save (Shift+Enter)
              </button>
              <button
                onClick={() => saveAndAdvance(true)}
                disabled={saving}
                style={{
                  flex: 1,
                  background: '#28a745',
                  color: '#fff',
                  border: 'none',
                  padding: '12px',
                  cursor: 'pointer',
                  borderRadius: '4px',
                  opacity: saving ? 0.7 : 1
                }}
              >
                {saving ? 'Saving...' : 'Save & Next (Enter)'}
              </button>
            </div>

            <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #eee' }}>
              <strong>Progress</strong>
              <p style={{ margin: '8px 0', color: '#666' }}>
                {savedMappings.size} pages with mappings
              </p>
              <div style={{ height: '8px', background: '#e9ecef', borderRadius: '4px', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${(savedMappings.size / numPages) * 100}%`, background: '#28a745' }} />
              </div>
            </div>

            {savedMappings.size > 0 && (
              <button
                onClick={() => navigate(`/admin/projects/${projectId}`)}
                style={{ marginTop: '16px', width: '100%', background: '#007bff', color: '#fff', border: 'none', padding: '12px', cursor: 'pointer', borderRadius: '4px' }}
              >
                Done - Return to Project
              </button>
            )}
          </div>
        </div>
      </main>

      {/* Bulk Mapping Modal */}
      {showBulkModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '8px',
            padding: '24px',
            width: '500px',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <h2 style={{ margin: '0 0 16px 0' }}>Map All Pages</h2>

            {bulkResults.size === 0 && !bulkProcessing && (
              <>
                <p style={{ color: '#666', marginBottom: '16px' }}>
                  Run OCR on a range of pages using the {storedRegions.length} defined region(s).
                </p>

                <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>Start Page</label>
                    <input
                      type="number"
                      min={1}
                      max={numPages}
                      value={bulkStartPage}
                      onChange={(e) => setBulkStartPage(Math.max(1, Math.min(numPages, parseInt(e.target.value) || 1)))}
                      style={{ width: '80px', padding: '8px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>End Page</label>
                    <input
                      type="number"
                      min={1}
                      max={numPages}
                      value={bulkEndPage}
                      onChange={(e) => setBulkEndPage(Math.max(1, Math.min(numPages, parseInt(e.target.value) || 1)))}
                      style={{ width: '80px', padding: '8px' }}
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => setShowBulkModal(false)}
                    style={{ padding: '8px 16px', border: '1px solid #ccc', background: '#fff', borderRadius: '4px', cursor: 'pointer' }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={runBulkOCR}
                    disabled={bulkStartPage > bulkEndPage}
                    style={{
                      padding: '8px 16px',
                      background: bulkStartPage > bulkEndPage ? '#ccc' : '#007bff',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: bulkStartPage > bulkEndPage ? 'not-allowed' : 'pointer'
                    }}
                  >
                    Run OCR
                  </button>
                </div>
              </>
            )}

            {bulkProcessing && (
              <div style={{ textAlign: 'center', padding: '24px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  border: '3px solid #e9ecef',
                  borderTopColor: '#007bff',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto 16px'
                }} />
                <p>Processing page {bulkProgress.current} of {bulkProgress.total}...</p>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              </div>
            )}

            {bulkResults.size > 0 && !bulkProcessing && (
              <>
                <p style={{ color: '#666', marginBottom: '16px' }}>
                  Found rooms on {bulkResults.size} pages. Review and save:
                </p>

                <div style={{ maxHeight: '300px', overflow: 'auto', border: '1px solid #ddd', borderRadius: '4px', marginBottom: '16px' }}>
                  {Array.from(bulkResults.entries()).map(([page, entries]) => (
                    <div key={page} style={{ padding: '8px 12px', borderBottom: '1px solid #eee' }}>
                      <strong>Page {page}:</strong>{' '}
                      {entries.map(e => e.number + (e.name ? ` ${e.name}` : '')).join(', ')}
                    </div>
                  ))}
                </div>

                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => { setBulkResults(new Map()); }}
                    style={{ padding: '8px 16px', border: '1px solid #ccc', background: '#fff', borderRadius: '4px', cursor: 'pointer' }}
                  >
                    Back
                  </button>
                  <button
                    onClick={() => setShowBulkModal(false)}
                    style={{ padding: '8px 16px', border: '1px solid #ccc', background: '#fff', borderRadius: '4px', cursor: 'pointer' }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveBulkResults}
                    style={{
                      padding: '8px 16px',
                      background: '#28a745',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Save All ({bulkResults.size} pages)
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
